/**
 * endGameSummary — 12/02/2026
 *
 * Construit le bilan de fin de partie à afficher AVANT le reset via
 * "Quitter la partie". Objectif : donner au joueur un sentiment de
 * clôture et matérialiser la progression (surtout utile en formation).
 *
 * Purement lecture — ne mute pas le state. Toutes les métriques sont
 * dérivées de state (yearlyReports, marketScaleFiredThresholds,
 * acquiredCompetitorIds, attritionHistory, firedDriversArchive,
 * ecoVadisHistory, unlockedAchievements).
 */
import { COMPETITORS } from '../data/competitors';

const NARRATIVE_LABELS = {
  1.15: 'Étoile montante du transport régional',
  1.30: 'Menace crédible pour les mastodontes',
  1.45: 'Prédateur alpha du secteur',
};

export function buildEndGameSummary(state) {
  if (!state) return null;

  const yearlyReports = Array.isArray(state.yearlyReports) ? state.yearlyReports : [];
  const totalRevenue = yearlyReports.reduce((s, r) => s + (r.totalRevenue || 0), 0);
  const totalExpenses = yearlyReports.reduce((s, r) => s + (r.totalExpenses || 0), 0);
  const totalNet = totalRevenue - totalExpenses;

  // Journées + années jouées.
  const totalDays = state.totalDays || 0;
  const yearsPlayed = Math.max(1, Math.floor(totalDays / 264)); // 22 j × 12 mois

  // Trésorerie finale + delta approximatif vs départ (0 par convention).
  const finalTreasury = Math.round(state.treasury || 0);
  const firstYearTreasuryStart = yearlyReports[0]?.treasuryStart ?? finalTreasury;
  const treasuryDelta = finalTreasury - firstYearTreasuryStart;

  // Flotte et effectif finaux (on ne peut pas trivialement compter "achetés
  // au total" sans event log dédié — on donne l'état final + départs cumulés).
  const finalFleet = (state.vehicles || []).length;
  const finalHeadcount = (state.drivers || []).length;
  const totalAttrition = (state.attritionHistory || []).length;
  const totalFired = Object.keys(state.firedDriversArchive || {}).length;
  const totalDeparted = totalAttrition + totalFired;

  // Rachats concurrents (M&A).
  const acquiredIds = state.acquiredCompetitorIds || [];
  const acquiredCompetitors = acquiredIds
    .map(id => COMPETITORS.find(c => c.id === id))
    .filter(Boolean)
    .map(c => ({ id: c.id, name: c.name }));

  // Moments-clés narratif presse.
  const pressThresholds = state.marketScaleFiredThresholds || [];
  const pressMilestones = pressThresholds
    .sort((a, b) => a - b)
    .map(t => ({ threshold: t, label: NARRATIVE_LABELS[t] || `Palier ${t}` }));

  // EcoVadis — dernier badge décroché (bronze/argent/or/platine).
  const ecoVadis = state.ecoVadisHistory || [];
  const lastEcoVadis = ecoVadis.length > 0 ? ecoVadis[ecoVadis.length - 1] : null;

  // Succès débloqués (nombre + liste courte).
  const achievements = state.unlockedAchievements || {};
  const achievementCount = Object.keys(achievements).length;

  // Meilleure et pire année (par résultat net).
  const bestYear = yearlyReports.length > 0
    ? yearlyReports.reduce((a, b) => (a.netResult || -Infinity) > (b.netResult || -Infinity) ? a : b)
    : null;
  const worstYear = yearlyReports.length > 0
    ? yearlyReports.reduce((a, b) => (a.netResult || Infinity) < (b.netResult || Infinity) ? a : b)
    : null;

  // Top clients via CRM loyalty.
  const clientLoyalty = state.clientLoyalty || {};
  const topClients = Object.entries(clientLoyalty)
    .map(([id, l]) => ({
      id,
      name: l.name || id,
      contracts: l.completedContracts || 0,
      totalRevenue: l.lifetimeRevenue || 0,
    }))
    .filter(c => c.contracts > 0)
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 3);

  return {
    company: state.company || { name: 'Sans nom' },
    phase: state.phase || 1,
    totalDays,
    yearsPlayed,
    finalTreasury,
    treasuryDelta,
    totalRevenue: Math.round(totalRevenue),
    totalExpenses: Math.round(totalExpenses),
    totalNet: Math.round(totalNet),
    finalFleet,
    finalHeadcount,
    totalDeparted,
    acquiredCompetitors,
    pressMilestones,
    lastEcoVadis,
    achievementCount,
    bestYear,
    worstYear,
    topClients,
  };
}
